import React, { useState } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import { TablePagination } from "@material-ui/core";
import tableIcons from "../../../../components/tables/MaterialTableIcons";
import CustomLoader from "../../../../components/CustomLoader";
import { useRecoilState } from "recoil";
import {
  paginationData,
  isPrevPageState,
} from "../../../../modules/CarrierAgreementMgmt/atoms";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { DataMappingBatch, StatusBucket } from "../types";
import { toast } from "components/toastNotification";
import { getLogsContent } from "../api/getLogsContent";
import { ProducerCodeLogsContentModal } from "../modals/ProducerCodeLogsContentModal";
import { ProducerCodeLogsContentModalView } from "../modals/ProducerCodeLogsContentModalView";
import { getCDRCStatusReconciliationFindings } from "../api/getCDRCStatusReconciliationFindings";

interface ProducerCodeNotificationTableProps {
  data: DataMappingBatch[];
  isLoading: boolean;
}

type NotificationTableRow = DataMappingBatch & {
  summary: string;
  key: number;
};

const PatchedPagination = ({
  ActionsComponent,
  onChangePage,
  onChangeRowsPerPage,
  ...tablePaginationProps
}: any) => {
  const currentPage = tablePaginationProps.page;

  const [page, setPage] = useRecoilState(paginationData);

  const [isPrevPage, setIsPrevPage] = useRecoilState(isPrevPageState);

  const sp = new URLSearchParams(window.location.search);

  const prevPage = sp.get("prevPage");

  const actionFrom = sp.get("actionFrom");

  React.useEffect(() => {
    if (actionFrom === null) {
      let initialPage: number;

      if (prevPage !== null) {
        if (
          !isPrevPage &&
          (Number(prevPage) !== currentPage || Number(prevPage) !== page)
        ) {
          initialPage = currentPage;
        } else {
          initialPage = Number(prevPage);

          setIsPrevPage(false);
        }
      } else {
        initialPage = currentPage;
      }

      setPage(initialPage);
    }
  }, [
    actionFrom,
    currentPage,
    page,
    prevPage,
    isPrevPage,
    setIsPrevPage,
    setPage,
  ]);

  return (
    <TablePagination
      {...tablePaginationProps}
      onPageChange={onChangePage}
      SelectProps={{
        IconComponent: () => <div />,
      }}
      onRowsPerPageChange={onChangeRowsPerPage}
      ActionsComponent={(subprops) => {
        const { onPageChange, ...actionsComponentProps } = subprops;

        return (
          <ActionsComponent
            {...actionsComponentProps}
            onChangePage={onPageChange}
          />
        );
      }}
    />
  );
};

const getIconClass = (bucket: StatusBucket): string => {
  if (bucket === "Succeeded") {
    return "text-green-500";
  }

  if (bucket === "Failed") {
    return "text-red-500";
  }

  return "text-amber-500";
};

const getFindingsPath = (
  mappingType: DataMappingBatch["mappingType"],
  createdDate: string,
): string => {
  const formatted = createdDate
    ? createdDate.substring(0, 10).replace(/-/g, "")
    : "";

  if (!formatted) {
    return "";
  }

  if (mappingType === "CDRC") {
    return `${formatted}-CDRCMappingProcessUnMapped.txt`;
  }

  if (mappingType === "AL3") {
    return `${formatted}-MappedToAL3Process.txt`;
  }

  return `${formatted}-KeeperMappingProcess.txt`;
};

const getNotificationMessage = (
  mappingType: DataMappingBatch["mappingType"],
): string => {
  if (mappingType === "CDRC") {
    return "CDRC Mapping Failed";
  }

  if (mappingType === "AL3") {
    return "AL3 Mapping Failed";
  }

  return "Keeper Mapping Failed";
};

const ProducerCodeNotificationTable: React.FC<
  ProducerCodeNotificationTableProps
> = ({ data, isLoading }) => {
  const [logsContent, setLogsContent] = useState("");

  const [isLogsView, setIsLogsView] = useState(false);

  const [notificationMessage, setNotificationMessage] = useState("");

  /*
   * Used only by
   * CDRC Status Reconciliation
   * Excel download.
   */
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const [isCDRCStatusReconciliation, setIsCDRCStatusReconciliation] =
    useState(false);

  const rows = React.useMemo<NotificationTableRow[]>(
    () =>
      (data ?? []).map((batch) => {
        const isCDRCSR = batch.mappingType === "CDRC Status Reconciliation";

        const summary = isCDRCSR
          ? `${batch.mappingType} ${batch.bucket} — ` +
            `${batch.total} rows ` +
            `(Corrected: ${batch.corrected}, ` +
            `Failed: ${batch.failed})`
          : `${batch.mappingType} Data Mapping ${batch.bucket} — ` +
            `${batch.total} rows ` +
            `(Succeeded: ${batch.succeeded}, ` +
            `Failed: ${batch.failed})`;

        return {
          ...batch,
          summary,
          key: batch.batchId,
        };
      }),
    [data],
  );

  const retrieveLogsContent = async (
    path: string,
    row: NotificationTableRow,
  ) => {
    try {
      let content = "";

      const isCDRCSR = row.mappingType === "CDRC Status Reconciliation";

      if (isCDRCSR) {
        const findings = await getCDRCStatusReconciliationFindings(row.batchId);

        content = findings.join("\n");
      } else {
        if (!path) {
          toast.fire(
            "Error",
            "Unable to determine the findings log path.",
            "error",
          );

          return;
        }

        content = await getLogsContent(path);
      }

      if (!content) {
        toast.fire("Error", "Unable to retrieve log content.", "error");

        return;
      }

      if (content === "File not exist" || content === "No findings found.") {
        toast.fire("Warning", content, "warning");

        return;
      }
      setSelectedBatchId(isCDRCSR ? row.batchId : null);

      setIsCDRCStatusReconciliation(isCDRCSR);

      setLogsContent(content);

      setNotificationMessage(
        isCDRCSR
          ? "CDRC Status Reconciliation Findings"
          : getNotificationMessage(row.mappingType),
      );

      setIsLogsView(true);
    } catch {
      toast.fire("Error", "Unable to retrieve log content.", "error");
    }
  };

  const onCloseModal = () => {
    setLogsContent("");

    setNotificationMessage("");

    setSelectedBatchId(null);

    setIsCDRCStatusReconciliation(false);

    setIsLogsView(false);
  };

  const columns = [
    {
      title: "Message",
      field: "summary",

      render: (row: NotificationTableRow) => {
        const Icon =
          row.bucket === "Succeeded"
            ? CheckCircleIcon
            : ExclamationTriangleIcon;

        return (
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 shrink-0 ${getIconClass(row.bucket)}`} />

            <span className="text-xs text-gray-700">{row.summary}</span>
          </div>
        );
      },
    },

    {
      title: "Findings",
      field: "findings",

      render: (row: NotificationTableRow) => {
        const isCDRCSR = row.mappingType === "CDRC Status Reconciliation";

        const hasFindings =
          isCDRCSR || row.bucket === "Failed" || row.bucket === "Partial";

        if (!hasFindings) {
          return null;
        }

        const path = isCDRCSR
          ? ""
          : getFindingsPath(row.mappingType, row.createdDate);
        return (
          <button
            type="button"
            onClick={() => retrieveLogsContent(path, row)}
            className="cursor-pointer text-xs italic text-gray-700 underline"
          >
            See Findings
          </button>
        );
      },
    },

    {
      title: "Created By",
      field: "createdBy",

      render: (row: NotificationTableRow) => (
        <span className="text-xs text-gray-700">{row.createdBy}</span>
      ),
    },

    {
      title: "Created Date",
      field: "createdDate",

      render: (row: NotificationTableRow) => {
        const dt = new Date(String(row.createdDate).replace(" ", "T"));

        const dateText = Number.isNaN(dt.getTime())
          ? row.createdDate
          : dt.toLocaleString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });

        return <span className="text-xs text-gray-500">{dateText}</span>;
      },
    },
  ];

  return (
    <>
      <div className="p-2">
        <MaterialTable
          title="Recent Data Mapping Activities"
          icons={tableIcons}
          columns={columns as any}
          data={rows as any}
          isLoading={isLoading}
          localization={{
            body: {
              emptyDataSourceMessage: isLoading ? (
                <CustomLoader />
              ) : (
                <span
                  style={{
                    fontSize: "14px",
                  }}
                >
                  No records to display.
                </span>
              ),
            },
          }}
          options={{
            showTitle: false,
            search: false,
            searchFieldAlignment: "left",
            toolbar: false,
            padding: "dense",
            paging: true,
            pageSize: 10,
            pageSizeOptions: [10, 20, 30, 50],
            headerStyle: {
              backgroundColor: "#fff",
              color: "#203A75",
              fontWeight: "bold",
              fontSize: "12px",
            },
            rowStyle: {
              fontSize: "13px",
              color: "#000",
            },
          }}
          components={{
            Pagination: PatchedPagination,

            OverlayLoading: () => <div />,

            Toolbar: (props) => (
              <div className="flex w-full items-center justify-start">
                <MTableToolbar {...props} />
              </div>
            ),
          }}
        />
      </div>

      {isLogsView && (
        <ProducerCodeLogsContentModal
          showFullView={isLogsView}
          closeFullView={onCloseModal}
        >
          <ProducerCodeLogsContentModalView
            logsContent={logsContent}
            notificationMessage={notificationMessage}
            batchId={selectedBatchId}
            isCDRCStatusReconciliation={isCDRCStatusReconciliation}
          />
        </ProducerCodeLogsContentModal>
      )}
    </>
  );
};

export default ProducerCodeNotificationTable;
